import { useState } from "react";
import { createPortal } from "react-dom";
import { Pencil, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useContent } from "@/context/ContentContext";
import RichTextEditor from "@/components/admin/RichTextEditor";
import { formatApiErrorDetail } from "@/lib/api";

function CmsModal({ title, onClose, onSave, saving, large, children }) {
  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" data-testid="cms-modal">
      <div className="absolute inset-0 bg-ink/70 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative bg-bone w-full ${large ? "max-w-3xl" : "max-w-lg"} max-h-[90vh] flex flex-col shadow-2xl`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink/10">
          <h3 className="font-serif-display text-xl">{title}</h3>
          <button onClick={onClose} className="text-ink-soft hover:text-claret transition-colors" data-testid="cms-close"><X size={20} /></button>
        </div>
        <div className="p-6 overflow-y-auto">{children}</div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-ink/10">
          <button onClick={onClose} className="px-5 py-2 text-sm uppercase tracking-wide border border-ink/20 hover:border-claret transition-colors" data-testid="cms-cancel">Annulla</button>
          <button onClick={onSave} disabled={saving} className="px-5 py-2 text-sm uppercase tracking-wide bg-claret text-bone hover:bg-claret-dark transition-colors disabled:opacity-60 flex items-center gap-2" data-testid="cms-save">
            {saving && <Loader2 size={14} className="animate-spin" />} Salva e pubblica
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export function EditableText({ id, defaultValue, as: Tag = "span", className = "", multiline = true, testid }) {
  const { content, isAdmin, save } = useContent();
  const value = content[id] ?? defaultValue;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const open = (e) => { e.preventDefault(); e.stopPropagation(); setDraft(value); setEditing(true); };
  const commit = async () => {
    setSaving(true);
    try { await save(id, draft); setEditing(false); toast.success("Testo aggiornato e pubblicato"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore nel salvataggio"); }
    finally { setSaving(false); }
  };

  return (
    <Tag className={`${className}${isAdmin ? " cms-editable" : ""}`} data-testid={testid}>
      {value}
      {isAdmin && (
        <button type="button" onClick={open} className="cms-pencil" title="Modifica testo" data-testid={`edit-${id}`}>
          <Pencil size={11} />
        </button>
      )}
      {editing && (
        <CmsModal title="Modifica testo" onClose={() => setEditing(false)} onSave={commit} saving={saving}>
          <textarea
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={multiline ? 5 : 2}
            className="w-full border border-ink/20 p-3 outline-none focus:border-claret transition-colors text-ink leading-relaxed"
            data-testid={`edit-input-${id}`}
          />
        </CmsModal>
      )}
    </Tag>
  );
}

export function EditableRich({ id, defaultHtml, className = "prose-blog", testid }) {
  const { content, isAdmin, save } = useContent();
  const html = content[id] ?? defaultHtml;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const commit = async () => {
    setSaving(true);
    try { await save(id, draft); setEditing(false); toast.success("Contenuto aggiornato e pubblicato"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore nel salvataggio"); }
    finally { setSaving(false); }
  };

  return (
    <div className={isAdmin ? "cms-editable-block" : ""} data-testid={testid}>
      <div className={className} dangerouslySetInnerHTML={{ __html: html }} />
      {isAdmin && (
        <button type="button" onClick={() => { setDraft(html); setEditing(true); }} className="cms-edit-btn" data-testid={`edit-${id}`}>
          <Pencil size={13} /> Modifica
        </button>
      )}
      {editing && (
        <CmsModal large title="Modifica contenuto" onClose={() => setEditing(false)} onSave={commit} saving={saving}>
          <RichTextEditor value={draft} onChange={setDraft} placeholder="Scrivi il contenuto…" />
        </CmsModal>
      )}
    </div>
  );
}
